import { cantidadDe } from '@/lib/utils/formato-precio';
import type { AppliedStrategy } from '../types/strategy-applications.types';

/**
 * Lote B2, punto 6 (spec 246; maqueta #b2-productos): cómo toma los productos
 * una estrategia, dicho en la tarjeta y en la ventana de sus productos. Por
 * categoría o marca toma sola los nuevos; elegidos a mano, no.
 */

type Grupo = AppliedStrategy['group'];

const lista = (xs: string[]) =>
  xs.length <= 1 ? (xs[0] ?? '') : `${xs.slice(0, -1).join(', ')} y ${xs[xs.length - 1]}`;

function deQue(g: Grupo): { categorias: string[]; marcas: string[] } {
  const f = (g.filter ?? {}) as { categories?: unknown; brands?: unknown };
  const strings = (v: unknown) => (Array.isArray(v) ? v.filter((x) => typeof x === 'string') : []);
  return { categorias: strings(f.categories), marcas: strings(f.brands) };
}

/** «de la marca Electrolux», «de las categorías Colchones y Sommiers», «de Colchones de la marca Maxi». */
export function deQueEnFrase(g: Grupo): string | null {
  const { categorias, marcas } = deQue(g);
  const cats =
    categorias.length === 0
      ? null
      : `${categorias.length === 1 ? 'de la categoría' : 'de las categorías'} ${lista(categorias)}`;
  const mar =
    marcas.length === 0
      ? null
      : `${marcas.length === 1 ? 'de la marca' : 'de las marcas'} ${lista(marcas)}`;
  if (cats && mar) return `de ${lista(categorias)} ${mar}`;
  return cats ?? mar;
}

/**
 * El enlace de la tarjeta: «11 productos elegidos», «37 productos de la marca
 * Electrolux». Una por categoría o marca que todavía no corrió no sabe cuántos:
 * «Por categoría o marca». Elegidos a mano: los que tiene hoy (los mismos que
 * cuenta la ventana: un SKU elegido que ya no está en el catálogo no cuenta); si
 * todavía no corrió, los elegidos (auditoría H19).
 */
export function aQuienEnLaTarjeta(e: Pick<AppliedStrategy, 'group' | 'productosHoy'>): string {
  if (e.group.kind === 'MANUAL')
    return cantidadDe(
      e.productosHoy ?? e.group.skus.length,
      'producto elegido',
      'productos elegidos'
    );
  if (e.productosHoy == null) return 'Por categoría o marca';
  const de = deQueEnFrase(e.group);
  return `${cantidadDe(e.productosHoy, 'producto', 'productos')}${de ? ` ${de}` : ' por categoría o marca'}`;
}

/** La línea de la tarjeta: «Toma sola los productos nuevos de Electrolux. 2 entraron esta semana.» */
export function comoTomaEnLaTarjeta(
  e: Pick<AppliedStrategy, 'group' | 'entraronEstaSemana' | 'productosHoy'>
): string {
  if (e.group.kind === 'MANUAL') return 'Elegidos a mano: un producto nuevo no entra solo.';
  const { categorias, marcas } = deQue(e.group);
  const nombres = [...categorias, ...marcas];
  const n = e.entraronEstaSemana ?? 0;
  // Auditoría H19: categoría y marca juntas con «de Colchones de la marca Maxi»
  // («de Colchones y Maxi» sonaba a cualquiera de las dos).
  const de =
    categorias.length > 0 && marcas.length > 0
      ? ` ${deQueEnFrase(e.group)}`
      : nombres.length
        ? ` de ${lista(nombres)}`
        : '';
  // Ronda final B2: sin saber cuántos tiene hoy (no corrió todavía, o un
  // servidor viejo), no se dice cuántos entraron: «ninguno» sería inventado.
  if (e.productosHoy == null) return `Toma sola los productos nuevos${de}.`;
  return `Toma sola los productos nuevos${de}. ${
    n === 0 ? 'Esta semana no entró ninguno.' : `${cantidadDe(n, 'entró', 'entraron')} esta semana.`
  }`;
}

/** El aviso de arriba de la ventana de productos. */
export function avisoDeComoToma(
  e: Pick<AppliedStrategy, 'group'>,
  entraronEstaSemana: number
): { titulo: string; texto: string } {
  if (e.group.kind === 'MANUAL')
    return {
      titulo: 'Los elegiste uno por uno',
      texto:
        'Un producto nuevo no entra solo: hay que agregarlo a mano. Las estrategias por categoría o marca, en cambio, toman solas los productos nuevos.',
    };
  const de = deQueEnFrase(e.group);
  return {
    titulo: `Toma sola los productos nuevos${de ? ` ${de}` : ''}`,
    texto: `Un producto nuevo que cumple el filtro entra solo en la próxima corrida. ${
      entraronEstaSemana === 0
        ? 'Esta semana no entró ninguno.'
        : `${cantidadDe(entraronEstaSemana, 'entró', 'entraron')} esta semana.`
    } Una estrategia con productos elegidos a mano, en cambio, no toma los nuevos.`,
  };
}
